import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  IconButton,
  Grid,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  DialogTitle,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Edit, CheckCircle, Save, Delete, Error } from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getNV, updatePatente, getTrucks } from "../API/autocompleteData";

const NotasVenta = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPatente, setSelectedPatente] = useState(null);
  const [selectedTipo, setSelectedTipo] = useState(null);
  const [selectedDeposito, setSelectedDeposito] = useState(null);
  const [selectedColor, setSelectedColor] = useState("Sin color");
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [editingPatente, setEditingPatente] = useState("");
  const [editingPatenteDos, setEditingPatenteDos] = useState("");
  const [savingPatente, setSavingPatente] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingNota, setEditingNota] = useState(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const queryClient = useQueryClient();

  const {
    data: notasVenta = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["notasVenta"],
    queryFn: getNV,
    retry: 2,
  });

  const {
    data: trucks = [],
    isLoading: trucksLoading,
    error: trucksError,
  } = useQuery({
    queryKey: ["trucks"],
    queryFn: getTrucks,
    staleTime: 10 * 60 * 1000, // 10 minutos
    retry: 2,
  });

  const updatePatenteMutation = useMutation({
    mutationFn: updatePatente,
    onSuccess: () => {
      queryClient.invalidateQueries(["notasVenta"]);
      setSuccessModalOpen(true);
    },
    onError: (error) => {
      // Mostrar la respuesta del endpoint si está disponible
      let errorMessage = "Error al actualizar la patente. Intente nuevamente.";

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      showError(errorMessage);
      throw error;
    },
  });

  const uniquePatentes = useMemo(() => {
    const patentes = notasVenta
      .map((nota) => nota.Patente)
      .filter((patente) => patente && patente.trim() !== "");
    return [...new Set(patentes)].sort();
  }, [notasVenta]);

  const uniqueDepositos = useMemo(() => {
    const depositos = notasVenta
      .map((nota) => nota.Deposito)
      .filter((dep) => dep && dep.trim && dep.trim() !== "");
    return [...new Set(depositos)].sort();
  }, [notasVenta]);
  
  const getRowStatus = (nota) => {
    const isNV = nota?.CodigoNotaVenta === 'NV';
    const fechaNV = nota?.FechaNV;
    if (!isNV) return "Sin color";
    if (!fechaNV) return "Sin color";

    const fecha = new Date(fechaNV);
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const vencimiento = new Date(fecha);
    vencimiento.setHours(0, 0, 0, 0);

  const diffDias = Math.floor((hoy - vencimiento) / (1000 * 60 * 60 * 24));

  if (diffDias > 1 && diffDias <= 3) return "Amarillo";
  if (diffDias <= 1) return "Verde";
  return "Rojo";
  };

  const getRowBgColor = (nota) => {
    const status = getRowStatus(nota);
    switch (status) {
      case "Sin color":
        return 'inherit';
      case "Amarillo":
        return "rgba(255, 213, 79, 0.50)";
      case "Rojo":
        return "rgba(244, 67, 54, 0.50)";
      default:
        return "rgba(79, 235, 84, 0.50)";
    }
  };

  const getRowAccentColor = (nota) => {
    const status = getRowStatus(nota);
    switch (status) {
      case "Verde":
        return "#43a047"; // green 600
      case "Amarillo":
        return "#fbc02d"; // yellow 700
      case "Rojo":
        return "#e53935"; // red 600
      default:
        return "#fff";
    }
  };

  const filteredNotas = useMemo(() => {
    let filtered = notasVenta;

    if (searchTerm !== "") {
      filtered = filtered.filter((nota) =>
        nota.NumeroNotaVenta.toString().includes(searchTerm)
      );
    }

    if (selectedPatente) {
      filtered = filtered.filter((nota) => nota.Patente === selectedPatente);
    }

    if (selectedTipo) {
      filtered = filtered.filter((nota) =>
        nota.CodigoNotaVenta?.startsWith(selectedTipo)
      );
    }

    if (selectedDeposito) {
      filtered = filtered.filter((nota) => nota.Deposito === selectedDeposito);
    }

    if (selectedColor && selectedColor !== "Sin color") {
      filtered = filtered.filter((nota) => getRowStatus(nota) === selectedColor);
    }

    return filtered;
  }, [notasVenta, searchTerm, selectedPatente, selectedTipo, selectedDeposito, selectedColor]);

  // Opciones de color dinámicas basadas en los filtros aplicados (excepto el color)
  const preColorFilteredNotas = useMemo(() => {
    let filtered = notasVenta;

    if (searchTerm !== "") {
      filtered = filtered.filter((nota) =>
        nota.NumeroNotaVenta.toString().includes(searchTerm)
      );
    }

    if (selectedPatente) {
      filtered = filtered.filter((nota) => nota.Patente === selectedPatente);
    }

    if (selectedTipo) {
      filtered = filtered.filter((nota) =>
        nota.CodigoNotaVenta?.startsWith(selectedTipo)
      );
    }

    if (selectedDeposito) {
      filtered = filtered.filter((nota) => nota.Deposito === selectedDeposito);
    }

    return filtered;
  }, [notasVenta, searchTerm, selectedPatente, selectedTipo, selectedDeposito]);

  const colorOrder = ["Sin color", "Verde", "Amarillo", "Rojo"];
  const colorOptions = useMemo(() => {
    const set = new Set(preColorFilteredNotas.map((n) => getRowStatus(n)));
    const opts = Array.from(set);
    if (!opts.includes("Sin color")) opts.push("Sin color");
    // ordenar por orden definido
    return opts.sort((a, b) => colorOrder.indexOf(a) - colorOrder.indexOf(b));
  }, [preColorFilteredNotas]);

  // Si el color seleccionado ya no está disponible, limpiarlo
  useEffect(() => {
    if (selectedColor && !colorOptions.includes(selectedColor)) {
      setSelectedColor("Sin color");
    }
  }, [colorOptions, selectedColor]);

  const getCircleStyles = (status) => {
    let bg = "transparent";
    let border = "1px solid #9e9e9e"; // por defecto para "Sin color"
    switch (status) {
      case "Verde":
        bg = "#43a047";
        border = "1px solid #43a047";
        break;
      case "Amarillo":
        bg = "#fbc02d";
        border = "1px solid #fbc02d";
        break;
      case "Rojo":
        bg = "#e53935";
        border = "1px solid #e53935";
        break;
      default:
        // Sin color u otros
        break;
    }
    return {
      width: 12,
      height: 12,
      borderRadius: "50%",
      backgroundColor: bg,
      border,
      display: "inline-block",
    };
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handlePatenteChange = (event, newValue) => {
    setSelectedPatente(newValue);
  };

  const handleEditClick = (nota) => {
    setEditingNota(nota);
    setEditingPatente(nota.Patente || "");
    setEditingPatenteDos(nota.PatenteDos || "");
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingNota(null);
    setEditingPatente("");
    setEditingPatenteDos("");
  };

  const handleSavePatenteModal = async () => {
    if (!editingPatente.trim() && !editingPatenteDos.trim()) {
      showError("Por favor ingrese al menos una patente válida");
      return;
    }

    setSavingPatente(true);
    try {
      const data = {
        patente: editingPatente.trim(),
        patenteDos: editingPatenteDos.trim(),
        codigoNv: editingNota.CodigoNotaVenta,
        numeroNv: editingNota.NumeroNotaVenta,
      };

      await updatePatenteMutation.mutateAsync(data);
      handleCloseEditModal();
    } catch (error) {
      console.error("Error al actualizar la patente:", error);

      // Mostrar la respuesta del endpoint si está disponible
      let errorMessage = "Error al actualizar la patente. Intente nuevamente.";

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      showError(errorMessage);
    } finally {
      setSavingPatente(false);
    }
  };

  const handleClearPatenteModal = async () => {
    setSavingPatente(true);
    try {
      const data = {
        patente: "", // String vacío para limpiar la patente
        patenteDos: "", // String vacío para limpiar la patente dos
        codigoNv: editingNota.CodigoNotaVenta,
        numeroNv: editingNota.NumeroNotaVenta,
      };

      await updatePatenteMutation.mutateAsync(data);
      handleCloseEditModal();
    } catch (error) {
      console.error("Error al limpiar la patente:", error);

      // Mostrar la respuesta del endpoint si está disponible
      let errorMessage = "Error al limpiar la patente. Intente nuevamente.";

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      showError(errorMessage);
    } finally {
      setSavingPatente(false);
    }
  };

  const handleCloseSuccessModal = () => {
    setSuccessModalOpen(false);
  };

  const handleCloseErrorModal = () => {
    setErrorModalOpen(false);
    setErrorMessage("");
  };

  const showError = (message) => {
    setErrorMessage(message);
    setErrorModalOpen(true);
  };

  const patentesOptions = trucks.map((truck) => truck.Patente) || [];

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ padding: 2 }}>
        <Typography variant="h6" color="error">
          Error al cargar las notas de venta
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {error.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 2 }}>
      <Box
        sx={{
          display: "flex",
          gap: { xs: 1, sm: 2 },
          marginBottom: { xs: 2, sm: 3 },
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        <TextField
          label="Nota de venta"
          variant="outlined"
          type="number"
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{
            width: { xs: "48%", sm: "300px" },
            minWidth: "120px",
            flex: { xs: "1 1 48%", sm: "0 0 auto" },
          }}
          placeholder="Número de nota de venta"
        />
        <Autocomplete
          options={uniquePatentes}
          value={selectedPatente}
          onChange={handlePatenteChange}
          size="small"
          renderInput={(params) => (
            <TextField {...params} label="Patente" placeholder="Patente..." />
          )}
          sx={{
            width: { xs: "48%", sm: "300px" },
            minWidth: "120px",
            flex: { xs: "1 1 48%", sm: "0 0 auto" },
          }}
          clearOnEscape
          blurOnSelect
        />
        <Autocomplete
          options={["NV", "NVA", "SCV"]}
          value={selectedTipo}
          onChange={(e, newValue) => setSelectedTipo(newValue)}
          size="small"
          renderInput={(params) => (
            <TextField {...params} label="Tipo" placeholder="NV, NVA o SCV" />
          )}
          sx={{
            width: { xs: "48%", sm: "300px" },
            minWidth: "120px",
            flex: { xs: "1 1 48%", sm: "0 0 auto" },
          }}
          clearOnEscape
          blurOnSelect
        />
        <Autocomplete
          options={uniqueDepositos}
          value={selectedDeposito}
          onChange={(e, newValue) => setSelectedDeposito(newValue)}
          size="small"
          renderInput={(params) => (
            <TextField {...params} label="Depósito" placeholder="Depósito..." />
          )}
          sx={{
            width: { xs: "48%", sm: "300px" },
            minWidth: "120px",
            flex: { xs: "1 1 48%", sm: "0 0 auto" },
          }}
          clearOnEscape
          blurOnSelect
        />
        <FormControl
          size="small"
          sx={{
            width: { xs: "48%", sm: "300px" },
            minWidth: "80px",
            width: "80px",
            flex: { xs: "1 1 48%", sm: "0 0 auto" },
          }}
        >
          <InputLabel id="color-select-label">Color</InputLabel>
          <Select
            labelId="color-select-label"
            id="color-select"
            value={selectedColor || "Sin color"}
            label="Color"
            displayEmpty
            onChange={(e) => setSelectedColor(e.target.value)}
            renderValue={(value) => (
              <Box sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Box sx={{ ...getCircleStyles(value || "Sin color") }} />
              </Box>
            )}
            sx={{
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
            }}
          >
            {colorOptions.map((opt) => (
              <MenuItem key={opt} value={opt} title={opt} sx={{ display: "flex", justifyContent: "center" }}>
                <Box sx={{ ...getCircleStyles(opt) }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {filteredNotas.length === 0 && !isLoading ? (
        <Typography variant="body1" color="text.secondary">
          {searchTerm || selectedPatente
            ? "No se encontraron notas de venta que coincidan con los filtros aplicados."
            : "No se encontraron notas de venta."}
        </Typography>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            mt: 2,
            maxHeight: { xs: "70vh", sm: "75vh", md: "80vh" },
            overflow: "auto",
            "& .MuiTableHead-root": {
              position: "sticky",
              top: 0,
              zIndex: 10,
              backgroundColor: "background.paper",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              borderBottom: "1px solid",
              borderColor: "divider",
            },
            // Mejores barras de scroll para móvil
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "rgba(0,0,0,0.1)",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0,0,0,0.3)",
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.5)",
              },
            },
            // Para Firefox
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(0,0,0,0.3) rgba(0,0,0,0.1)",
          }}
        >
          <Table
            sx={{
              minWidth: { xs: "100%", sm: 650 },
              "& .MuiTableCell-root": {
                padding: { xs: "8px 4px", sm: "16px" },
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                whiteSpace: { xs: "nowrap", sm: "normal" },
              },
            }}
            aria-label="notas de venta table"
            stickyHeader
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Código</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Número</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Depósito</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Cliente</TableCell>

                <TableCell sx={{ fontWeight: "bold" }}>Producto</TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                  }}
                >
                  Litros
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                  }}
                >
                  Dirección
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Patente</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredNotas.map((nota, index) => (
                <TableRow
                  key={index}
                  hover
                  sx={{
                    backgroundColor: getRowBgColor(nota),
                    borderLeft: `4px solid ${getRowAccentColor(nota)}`,
                    transition: "background-color 0.2s ease",
                  }}
                >
                  <TableCell
                    sx={{
                      maxWidth: { xs: "80px", sm: "100px" },
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      minWidth: { xs: "60px", sm: "80px" },
                    }}
                  >
                    {nota.CodigoNotaVenta}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "medium",
                      minWidth: { xs: "70px", sm: "90px" },
                    }}
                  >
                    {nota.NumeroNotaVenta}
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: { xs: "80px", sm: "110px" },
                      fontSize: { xs: "0.7rem", sm: "inherit" },
                    }}
                  >
                    {nota.FechaNV
                      ? new Date(nota.FechaNV).toLocaleDateString("es-AR")
                      : "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: { xs: "70px", sm: "90px" },
                      fontSize: { xs: "0.7rem", sm: "inherit" },
                    }}
                  >
                    {nota.Deposito || "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: { xs: "120px", sm: "200px" },
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      minWidth: { xs: "100px", sm: "150px" },
                    }}
                  >
                    {nota.RazonSocialCliente}
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: { xs: "90px", sm: "130px" },
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      minWidth: { xs: "70px", sm: "100px" },
                      width: { xs: "90px", sm: "130px" },
                    }}
                  >
                    {nota.DescripcionProducto}
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: { xs: "50px", sm: "70px" },
                      maxWidth: { xs: "60px", sm: "80px" },
                      width: { xs: "55px", sm: "75px" },
                      fontSize: { xs: "0.7rem", sm: "inherit" },
                    }}
                  >
                    {nota.Litros?.toLocaleString()} L
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: { xs: "100px", sm: "150px" },
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      minWidth: { xs: "80px", sm: "120px" },
                    }}
                  >
                    {nota.DireccionEntrega}
                  </TableCell>
                  <TableCell sx={{ minWidth: { xs: 100, sm: 120 } }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: { xs: 0.25, sm: 0.5 },
                        justifyContent: "space-between",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          color: (nota.Patente || nota.PatenteDos)
                            ? "text.primary"
                            : "text.secondary",
                          overflow: "hidden",
                          maxWidth: { xs: "60px", sm: "120px" },
                        }}
                      >
                        {nota.Patente || nota.PatenteDos ? (
                          <>
                            {nota.Patente && (
                              <Box
                                sx={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  fontSize: { xs: "0.7rem", sm: "0.875rem" },
                                }}
                              >
                                {nota.Patente}
                              </Box>
                            )}
                            {nota.PatenteDos && (
                              <Box
                                sx={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  fontSize: { xs: "0.7rem", sm: "0.875rem" },
                                }}
                              >
                                {nota.PatenteDos}
                              </Box>
                            )}
                          </>
                        ) : (
                          <Box sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}>
                            Sin patente
                          </Box>
                        )}
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick(nota)}
                        color="primary"
                        sx={{
                          p: { xs: 0.125, sm: 0.25 },
                          minWidth: "auto",
                          "& .MuiSvgIcon-root": {
                            fontSize: { xs: "16px", sm: "20px" },
                          },
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={editModalOpen}
        onClose={handleCloseEditModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            margin: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Editar Patente
          <IconButton
            onClick={handleClearPatenteModal}
            disabled={savingPatente}
            color="error"
            size="small"
          >
            {savingPatente ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <Delete />
            )}
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          {editingNota && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Nota de Venta: <strong>{editingNota.NumeroNotaVenta}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Cliente: {editingNota.RazonSocialCliente}
              </Typography>

              <Autocomplete
                options={patentesOptions}
                value={editingPatente === "" ? null : editingPatente}
                onChange={(event, newValue) => {
                  setEditingPatente(newValue || "");
                }}
                loading={trucksLoading}
                disabled={savingPatente}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Patente"
                    placeholder="Seleccione una patente..."
                    fullWidth
                    autoFocus
                  />
                )}
                clearOnEscape
                blurOnSelect
                sx={{ mb: 2 }}
              />
              {editingNota.CodigoNotaVenta === "NVA" && (
                <Autocomplete
                  options={patentesOptions}
                  value={editingPatenteDos === "" ? null : editingPatenteDos}
                  onChange={(event, newValue) => {
                    setEditingPatenteDos(newValue || "");
                  }}
                  loading={trucksLoading}
                  disabled={savingPatente}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Patente Dos"
                      placeholder="Seleccione una segunda patente..."
                      fullWidth
                    />
                  )}
                  clearOnEscape
                  blurOnSelect
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleCloseEditModal}
            disabled={savingPatente}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSavePatenteModal}
            disabled={savingPatente}
            variant="contained"
          >
            {savingPatente ? "Guardando..." : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={successModalOpen}
        onClose={handleCloseSuccessModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        <DialogContent sx={{ textAlign: "center", py: 4 }}>
          <Box sx={{ mb: 2 }}>
            <CheckCircle
              sx={{
                fontSize: 60,
                color: "success.main",
                mb: 2,
              }}
            />
            <Typography variant="body1" color="text.secondary">
              La patente ha sido actualizada exitosamente
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            onClick={handleCloseSuccessModal}
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1,
            }}
          >
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={errorModalOpen}
        onClose={handleCloseErrorModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        <DialogContent sx={{ textAlign: "center", py: 4 }}>
          <Box sx={{ mb: 2 }}>
            <Error
              sx={{
                fontSize: 60,
                color: "error.main",
                mb: 2,
              }}
            />
            <Typography variant="body1" color="text.secondary">
              {errorMessage}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            onClick={handleCloseErrorModal}
            variant="contained"
            color="error"
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1,
            }}
          >
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotasVenta;
